import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Search, ExternalLink, Zap, Users, Server, Shield, Star } from 'lucide-react'

interface Bot {
  id: string
  name: string
  short_description: string
  description: string
  avatar_url: string
  invite_url: string
  website_url: string
  prefix: string
  guild_count: number
  user_count: number
  ping: number
  tags: string[]
  verified: boolean
  featured: boolean
  stats_updated_at: string
}

export default function BotDirectory() {
  const navigate = useNavigate()
  const [bots, setBots] = useState<Bot[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const [selected, setSelected] = useState<Bot | null>(null)

  useEffect(() => {
    fetch('/api/bots')
      .then(r => r.json())
      .then(d => { setBots(d.bots || []); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  const allTags = Array.from(new Set(bots.flatMap(b => b.tags || []))).sort()

  const filtered = bots.filter(b => {
    const q = search.toLowerCase()
    const matchSearch = !q || b.name.toLowerCase().includes(q) || b.short_description?.toLowerCase().includes(q) || b.tags?.some(t => t.includes(q))
    const matchTag = !activeTag || b.tags?.includes(activeTag)
    return matchSearch && matchTag
  })

  const fmt = (n: number) => n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n)

  if (selected) {
    return (
      <div className="min-h-screen bg-aethex-bg text-aethex-text font-mono">
        <div className="max-w-3xl mx-auto px-6 py-10">
          <button
            onClick={() => setSelected(null)}
            className="flex items-center gap-2 text-xs text-aethex-dim hover:text-aethex-primary uppercase tracking-widest mb-8 transition-colors"
          >
            <ArrowLeft size={13} /> Back to Directory
          </button>

          <div className="border border-aethex-border bg-aethex-panel p-8 mb-6">
            <div className="flex items-start gap-6 mb-6">
              <img
                src={selected.avatar_url || '/logo.png'}
                alt={selected.name}
                className="w-20 h-20 rounded-none border border-aethex-border object-cover flex-shrink-0"
                onError={(e) => { (e.target as HTMLImageElement).src = '/logo.png' }}
              />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <h1 className="font-display text-2xl font-bold text-aethex-text uppercase tracking-wider">{selected.name}</h1>
                  {selected.verified && (
                    <span className="flex items-center gap-1 text-xs text-aethex-primary border border-aethex-primary/40 px-2 py-0.5 bg-aethex-primary/5">
                      <Shield size={10} /> Verified
                    </span>
                  )}
                  {selected.featured && (
                    <span className="flex items-center gap-1 text-xs text-yellow-400 border border-yellow-400/30 px-2 py-0.5 bg-yellow-400/5">
                      <Star size={10} /> Featured
                    </span>
                  )}
                </div>
                <p className="text-aethex-dim text-sm mb-4">{selected.short_description}</p>
                <div className="flex gap-2 flex-wrap">
                  {(selected.tags || []).map(tag => (
                    <span key={tag} className="text-xs text-aethex-dim border border-aethex-border px-2 py-0.5">{tag}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-px bg-aethex-border mb-6">
              <div className="bg-aethex-surface px-4 py-3">
                <div className="flex items-center gap-2 text-aethex-dim text-xs uppercase tracking-widest mb-1"><Server size={11} /> Servers</div>
                <div className="font-display text-xl text-aethex-primary">{fmt(selected.guild_count || 0)}</div>
              </div>
              <div className="bg-aethex-surface px-4 py-3">
                <div className="flex items-center gap-2 text-aethex-dim text-xs uppercase tracking-widest mb-1"><Users size={11} /> Users</div>
                <div className="font-display text-xl text-aethex-primary">{fmt(selected.user_count || 0)}</div>
              </div>
              <div className="bg-aethex-surface px-4 py-3">
                <div className="flex items-center gap-2 text-aethex-dim text-xs uppercase tracking-widest mb-1"><Zap size={11} /> Ping</div>
                <div className={`font-display text-xl ${(selected.ping || 0) < 100 ? 'text-green-400' : (selected.ping || 0) < 250 ? 'text-yellow-400' : 'text-red-400'}`}>
                  {selected.ping > 0 ? selected.ping + 'ms' : '—'}
                </div>
              </div>
            </div>

            {selected.description && (
              <p className="text-sm text-aethex-dim leading-relaxed mb-6 border-l-2 border-aethex-primary/30 pl-4">
                {selected.description}
              </p>
            )}

            <div className="flex gap-3 flex-wrap">
              {selected.invite_url && (
                <a
                  href={selected.invite_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2 border border-aethex-primary text-aethex-primary text-xs uppercase tracking-widest hover:bg-aethex-primary/10 glow transition-all"
                >
                  Add to Server <ExternalLink size={12} />
                </a>
              )}
              {selected.website_url && (
                <a
                  href={selected.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2 border border-aethex-border text-aethex-dim text-xs uppercase tracking-widest hover:border-aethex-primary/50 hover:text-aethex-primary transition-all"
                >
                  Website <ExternalLink size={12} />
                </a>
              )}
            </div>
          </div>

          {selected.stats_updated_at && (
            <p className="text-xs text-aethex-dim text-right">
              Stats updated {new Date(selected.stats_updated_at).toLocaleString()}
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-aethex-bg text-aethex-text font-mono">
      {/* Header */}
      <div className="border-b border-aethex-border bg-aethex-panel">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/app')}
              className="text-aethex-dim hover:text-aethex-primary transition-colors"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <div className="font-display text-xs text-aethex-primary uppercase tracking-widest mb-0.5">AeThex Connect</div>
              <h1 className="font-display text-lg font-bold uppercase tracking-wider text-aethex-text">Bot Directory</h1>
            </div>
          </div>
          <div className="text-xs text-aethex-dim">{bots.length} bot{bots.length !== 1 ? 's' : ''} registered</div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Search + tag filters */}
        <div className="mb-6 space-y-3">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-aethex-dim" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search bots..."
              className="w-full bg-aethex-surface border border-aethex-border text-aethex-text font-mono text-sm pl-9 pr-4 py-2.5 focus:outline-none focus:border-aethex-primary/50 placeholder-aethex-dim transition-colors"
            />
          </div>
          {allTags.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setActiveTag(null)}
                className={`text-xs px-3 py-1 border uppercase tracking-wider transition-all ${!activeTag ? 'border-aethex-primary text-aethex-primary bg-aethex-primary/5' : 'border-aethex-border text-aethex-dim hover:border-aethex-primary/40 hover:text-aethex-primary'}`}
              >
                All
              </button>
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                  className={`text-xs px-3 py-1 border uppercase tracking-wider transition-all ${activeTag === tag ? 'border-aethex-primary text-aethex-primary bg-aethex-primary/5' : 'border-aethex-border text-aethex-dim hover:border-aethex-primary/40 hover:text-aethex-primary'}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-aethex-primary border-t-transparent animate-spin mx-auto mb-4" />
              <p className="text-xs text-aethex-dim uppercase tracking-widest">Loading bots...</p>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 border border-aethex-border bg-aethex-panel">
            <div className="text-3xl mb-4">🤖</div>
            <p className="text-xs text-aethex-dim uppercase tracking-widest">{search || activeTag ? 'No bots match your search.' : 'No bots registered yet.'}</p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map(bot => (
              <div
                key={bot.id}
                onClick={() => setSelected(bot)}
                className="group border border-aethex-border bg-aethex-panel hover:border-aethex-primary/40 hover:bg-aethex-surface cursor-pointer transition-all"
              >
                {/* Featured banner */}
                {bot.featured && (
                  <div className="flex items-center gap-1.5 px-4 py-1.5 bg-yellow-400/5 border-b border-yellow-400/20 text-yellow-400 text-xs uppercase tracking-widest">
                    <Star size={10} /> Featured
                  </div>
                )}

                <div className="p-5">
                  <div className="flex items-start gap-4 mb-4">
                    <img
                      src={bot.avatar_url || '/logo.png'}
                      alt={bot.name}
                      className="w-12 h-12 border border-aethex-border object-cover flex-shrink-0"
                      onError={(e) => { (e.target as HTMLImageElement).src = '/logo.png' }}
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="font-display font-bold text-sm uppercase tracking-wide text-aethex-text group-hover:text-aethex-primary transition-colors truncate">
                          {bot.name}
                        </span>
                        {bot.verified && <Shield size={11} className="text-aethex-primary flex-shrink-0" />}
                      </div>
                      <p className="text-xs text-aethex-dim line-clamp-2 leading-relaxed">{bot.short_description}</p>
                    </div>
                  </div>

                  {/* Mini stats */}
                  <div className="flex items-center gap-4 text-xs text-aethex-dim mb-4">
                    <span className="flex items-center gap-1"><Server size={10} /> {fmt(bot.guild_count || 0)}</span>
                    <span className="flex items-center gap-1"><Users size={10} /> {fmt(bot.user_count || 0)}</span>
                    {bot.ping > 0 && (
                      <span className={`flex items-center gap-1 ${bot.ping < 100 ? 'text-green-400' : bot.ping < 250 ? 'text-yellow-400' : 'text-red-400'}`}>
                        <Zap size={10} /> {bot.ping}ms
                      </span>
                    )}
                  </div>

                  {/* Tags */}
                  <div className="flex gap-1.5 flex-wrap">
                    {(bot.tags || []).slice(0, 4).map(tag => (
                      <span key={tag} className="text-xs text-aethex-dim border border-aethex-border px-1.5 py-0.5">{tag}</span>
                    ))}
                    {(bot.tags || []).length > 4 && (
                      <span className="text-xs text-aethex-dim">+{bot.tags.length - 4}</span>
                    )}
                  </div>
                </div>

                <div className="px-5 py-3 border-t border-aethex-border flex items-center justify-between">
                  <span className="text-xs text-aethex-dim font-mono">prefix: {bot.prefix || '/'}</span>
                  <span className="text-xs text-aethex-primary uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">View →</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
